import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NeuralBridge } from "@/components/NeuralBridge";
import { BrainCircuit, ArrowRight } from "lucide-react";

const HERO = "/neocortex-hero.jpg";
const LOGO = "/octon-logo.png";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const authError = new URLSearchParams(window.location.search).get("error");

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  // Zero-config Emergent-managed Google login. No Google Cloud Console setup needed.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#050505] text-white">
      {/* HERO neocortex brain — right side, slightly smaller & fully shown */}
      <motion.div
        initial={{ opacity: 0, scale: 1.12 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.8, ease: "easeOut" }}
        className="absolute right-0 top-[12%] z-[1] h-[72%] w-full lg:w-[50%]"
      >
        <img src={HERO} alt="OCTON neocortex brain" className="h-full w-full object-contain object-right"
          style={{ maskImage: "radial-gradient(ellipse at 60% 50%, #000 55%, transparent 92%)", WebkitMaskImage: "radial-gradient(ellipse at 60% 50%, #000 55%, transparent 92%)" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/20 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
      </motion.div>

      {/* faint animated neural bridge layered over the brain for life */}
      <div className="pointer-events-none absolute inset-0 z-[2] opacity-40 mix-blend-screen">
        <NeuralBridge className="h-full w-full" />
      </div>

      {/* HUD grid + scanline */}
      <div className="absolute inset-0 z-[2] hud-grid opacity-60" />
      <div className="scanline" />

      {/* corner brackets */}
      {["left-5 top-5 border-l-2 border-t-2", "right-5 top-5 border-r-2 border-t-2",
        "left-5 bottom-5 border-l-2 border-b-2", "right-5 bottom-5 border-r-2 border-b-2"].map((c, i) => (
        <div key={i} className={`pointer-events-none absolute z-[5] h-10 w-10 border-cyan-400/40 ${c}`} />
      ))}

      {/* content */}
      <div className="relative z-10 flex min-h-screen flex-col justify-end px-8 pb-16 sm:px-16 lg:justify-center lg:px-24">
        {/* logo + fleet tag */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="mb-6 flex items-center gap-4"
        >
          <motion.img
            src={LOGO} alt="OCTON logo" data-testid="octon-logo"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="h-20 w-20 sm:h-28 sm:w-28"
            style={{
              filter: "drop-shadow(0 10px 24px rgba(0,229,255,0.35)) drop-shadow(0 4px 10px rgba(0,0,0,0.6))",
            }}
          />
          <div>
            <p className="font-tac text-[10px] tracking-[0.4em] txt-cyan sm:text-xs">OCTON · COACH INTELLIGENCE</p>
            <p className="font-head text-2xl font-black leading-none tracking-tighter">OCTON</p>
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20, filter: "blur(12px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ delay: 0.4, duration: 0.9 }}
          className="font-head text-6xl font-black leading-[0.86] tracking-tighter drop-shadow-[0_4px_40px_rgba(0,0,0,0.95)] sm:text-7xl lg:text-8xl"
        >
          COACH
          <br />
          <span className="txt-cyan">COMPANION</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-white/75 drop-shadow-[0_2px_12px_rgba(0,0,0,0.95)]"
        >
          A living, breathing neocortex super-brain — not a smart monkey. Hebbian learning fires across the
          Hippocampus↔Neocortex bridge, a 512-dim embedding CNN and Bayesian propagation, trained on the
          Premier League, Champions League &amp; beyond.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
          className="mt-10"
        >
          {authError && (
            <div
              data-testid="login-auth-error"
              className="mb-5 max-w-md rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200 backdrop-blur-sm"
            >
              {authError}
            </div>
          )}
          <button
            data-testid="google-login-btn"
            onClick={handleLogin}
            className="group inline-flex items-center gap-3 rounded-full bg-white px-7 py-4 text-sm font-semibold text-black transition hover:shadow-[0_0_50px_rgba(0,229,255,0.65)]"
          >
            <img src="https://www.google.com/favicon.ico" alt="" className="h-5 w-5" />
            Continue with Google
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </button>
          <p className="mt-4 flex items-center gap-2 text-xs text-white/45">
            <BrainCircuit size={14} className="txt-cyan" /> Secure Google sign-on
          </p>
        </motion.div>
      </div>
    </div>
  );
}
